import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { getCachedTemplates } from '@/lib/stores/templatesStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getStatusColor } from '@/lib/utils/statusColors';
import { useNavigate } from 'react-router-dom';
import CardBase from '../cards/CardBase';

interface ObjectNodeData {
  itemId: string;
  itemType: 'object';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const ObjectNode = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as ObjectNodeData;
  
  // Get the actual object data
  const objects = selectedGame?.id ? getCachedObjects(selectedGame.id) : [];
  const object = objects.find(obj => obj.id === nodeData.itemId);
  
  // Get blueprint for color
  const templates = selectedGame?.id ? getCachedTemplates(selectedGame.id) : [];
  const template = object?.blueprint_id ? templates.find(tp => tp.id === object.blueprint_id) : null;
  
  // Get status color
  const statusColor = object?.status ? getStatusColor(object.status, 'objects', selectedGame?.id) : undefined;
  
  // Determine background color (blueprint color or default)
  const bgColor = template?.settings?.color || '#6366f1';
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (object?.id) {
      navigate(`/library/${object.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[200px] max-w-[300px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t"
        style={{ backgroundColor: bgColor as string }}
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-cube" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Expand"
        >
          <IconProvider iconstring="fa-expand" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Body with details */}
      <div className="px-3 py-2 space-y-1">
        {object?.type && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-tag" size={10} />
            <span className="truncate">{object.type}</span>
          </div>
        )}
        
        {object?.tags && object.tags.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-tags" size={10} />
            <span className="truncate">{object.tags.length} tag(s)</span>
          </div>
        )}
        
          {template && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-file-lines" size={10} />
            <span className="truncate">{template.name}</span>
          </div>
        )}
        
        {object?.status && (
          <div className="flex items-center gap-1 text-xs">
            <div 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: statusColor }}
            />
            <span className="truncate" style={{ color: statusColor }}>
              {object.status}
            </span>
          </div>
        )}
      </div>
    </CardBase>
  );
});

ObjectNode.displayName = 'ObjectNode';

